import { Alert, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { GqlPlannerVisitStatus, GqlWorkerVisitReportStatus } from "~/__generated__";
import { MapPanel, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";

import { ProjectReviewVisitV2RejectButton } from "../../components/ProjectReviewVisitV2RejectButton";
import { ProjectReviewVisitV2RejectionAlert } from "../../components/ProjectReviewVisitV2RejectionAlert";
import { useReviewVisitDecisionActions } from "../../hooks/useReviewVisitDecisionActions";
import { useProjectReviewVisitV2TeamReportsRoute_GetData } from "./api/useProjectReviewVisitV2TeamReportsRoute_GetData";
import { useProjectReviewVisitV2TeamReportsRoute_UpdateReportsStatus } from "./api/useProjectReviewVisitV2TeamReportsRoute_UpdateReportsStatus";
import { useProjectReviewVisitV2TeamReportsRoute_UpdateWorkerReport } from "./api/useProjectReviewVisitV2TeamReportsRoute_UpdateWorkerReport";
import ApprovalButton from "./components/ApprovalButton";
import TeamReportsEmptyState from "./components/TeamReportsEmptyState";
import { TeamReportsHeader } from "./components/TeamReportsHeader";
import TeamReportsList from "./components/TeamReportsList";
import { TeamReportsPanelSkeleton } from "./components/TeamReportsPanelSkeleton";
import { ProjectReviewVisitV2TeamReportsRoute_GetDataDocument } from "./queries.generated";

export function ProjectReviewVisitV2TeamReportsRoute() {
    const { t } = useTranslation("project");

    const navigate = useNavigate();
    const { clientSlug, projectSlug, visitId } = useParams();
    if (!clientSlug || !projectSlug || !visitId) throw new Error("invalid params");

    const { teamReportsData, loading } = useProjectReviewVisitV2TeamReportsRoute_GetData(visitId);
    const { handleReject, loading: decisionActionLoading } = useReviewVisitDecisionActions({
        visitId,
        refetchQueries: [ProjectReviewVisitV2TeamReportsRoute_GetDataDocument],
    });
    const { updateWorkerReport, error: updateWorkerReportError } =
        useProjectReviewVisitV2TeamReportsRoute_UpdateWorkerReport();
    const {
        updateReportsStatus,
        loading: updateReportsStatusLoading,
        error: updateReportsStatusError,
    } = useProjectReviewVisitV2TeamReportsRoute_UpdateReportsStatus();

    const handleUpdateAllStatusesClick = async (status: GqlWorkerVisitReportStatus) => {
        await updateReportsStatus(visitId, status);
        if (status === GqlWorkerVisitReportStatus.Approved) {
            navigate("../");
        }
    };

    const handleRejectClick = async (reason: string) => {
        await handleReject(reason);
        navigate("../");
    };

    const teamReportsStatuses = teamReportsData?.reportsV2.map(report => report.status);

    if (loading) return <TeamReportsPanelSkeleton backHref="../" />;

    return (
        <MapPanel>
            <TeamReportsHeader backHref="../" />
            <MapPanelScrollableArea>
                {!teamReportsData || teamReportsData.reportsV2.length === 0 ? (
                    <TeamReportsEmptyState />
                ) : (
                    <>
                        <TeamReportsList
                            data={teamReportsData}
                            error={updateWorkerReportError}
                            onUpdate={(reportId, values) => updateWorkerReport(reportId, values)}
                        />
                        <Stack paddingInline={2} paddingTop={2} rowGap={2}>
                            {updateReportsStatusError && (
                                <Alert severity="error">{t("projectReviewVisitV2.teamReportsRoute.errorMsg")}</Alert>
                            )}
                            {teamReportsData?.status !== GqlPlannerVisitStatus.Approved && (
                                <ApprovalButton
                                    teamReportsStatuses={teamReportsStatuses}
                                    onUpdate={status => handleUpdateAllStatusesClick(status)}
                                    loading={updateReportsStatusLoading}
                                />
                            )}
                        </Stack>
                    </>
                )}

                {teamReportsData && (
                    <>
                        <ProjectReviewVisitV2RejectionAlert visit={teamReportsData} />
                        <ProjectReviewVisitV2RejectButton
                            visit={teamReportsData}
                            onReject={handleRejectClick}
                            loading={decisionActionLoading}
                        />
                    </>
                )}
            </MapPanelScrollableArea>
        </MapPanel>
    );
}
